import { useMemo } from 'react';

import { collection, doc, DocumentReference } from 'firebase/firestore';

import { useDocumentData } from 'react-firebase-hooks/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';

import { useFirestoreError } from './useFirestoreError';

import type Org from '../types/Org';

export default function useOrg(orgId: string | undefined | null) {
  const { isGod } = useAuth();
  const { userState, getAllActiveOrgs } = useData();

  const orgRef = useMemo(() => {
    if (!orgId) return null;
    if (!isGod && !getAllActiveOrgs().includes(orgId)) return null;

    return doc(collection(firestore, 'org'), orgId) as DocumentReference<Org>;
  }, [orgId, isGod, getAllActiveOrgs]);

  const [org, isOrgLoading, orgError] = useDocumentData<Org>(orgRef);
  useFirestoreError('useOrg', orgError);

  if (orgId === null) {
    return {
      org: null,
      isOrgLoading: false,
    };
  }

  if (userState.status === 'logged_out') {
    return {
      org: null,
      isOrgLoading: false,
      orgError: new Error('User must be logged in to view organizations'),
    };
  }

  // Convert permission error to a proper error object
  if (!isOrgLoading && 
    !orgError && 
    !isGod && 
    orgId && 
    !getAllActiveOrgs().includes(orgId)) {
    return {
      org: null,
      isOrgLoading: false,
      orgError: new Error('Not authorized to access this organization'),
    };
  }

  return {
    org: org || null,
    isOrgLoading: isOrgLoading ||
      (userState.status === 'authenticating_user') ||
      (userState.status === 'authenticating_profile') ||
      (userState.status === 'authenticating_memberships'),
    orgError,
  };
}
